/**
 * Scenario Comparison Table Component
 *
 * Displays saved scenarios in a sortable table with best value highlighting
 */

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Button,
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Download as DownloadIcon } from '@mui/icons-material';
import CompareIcon from '@mui/icons-material/Compare';
import type { PoolScenario, ScenarioResults } from '../../types';
import { calculateCapacity } from '../../core/capacity';
import { calculateReliability } from '../../core/reliability';
import { calculatePerformance } from '../../core/performance';
import { calculateEconomics } from '../../core/economics';

interface Column {
  id: string;
  label: string;
  numeric: boolean;
  sortable: boolean;
  render: (scenario: PoolScenario, results: ScenarioResults) => string | number;
  compareBest: (results: ScenarioResults[], scenarios: PoolScenario[]) => number | null;
}

interface ScenarioTableProps {
  scenarios: PoolScenario[];
  onCompare?: (scenario: PoolScenario) => void;
}

function calculateScenarioResults(scenario: PoolScenario): ScenarioResults {
  const capacity = calculateCapacity(scenario.topology, scenario.drive, scenario.zfs, scenario.utilization);
  const reliability = calculateReliability(scenario.topology, scenario.drive, scenario.utilization, capacity.finalUsableTiB);
  const performance = calculatePerformance(scenario.topology, scenario.drive, scenario.performance);
  const economics = calculateEconomics(scenario.topology, scenario.drive, scenario.economics, capacity);

  return {
    capacity,
    reliability,
    performance,
    economics,
  };
}

export function ScenarioTable({ scenarios, onCompare }: ScenarioTableProps) {
  // Calculate results for all scenarios
  const resultsMap = new Map<string, ScenarioResults>();
  scenarios.forEach(scenario => {
    resultsMap.set(scenario.id, calculateScenarioResults(scenario));
  });

  // Find best values for highlighting
  const resultsArray = Array.from(resultsMap.values());

  // Define columns
  const columns: Column[] = [
    {
      id: 'name',
      label: 'Scenario',
      numeric: false,
      sortable: true,
      render: (scenario) => scenario.name,
      compareBest: () => null
    },
    {
      id: 'raidLevel',
      label: 'RAID',
      numeric: false,
      sortable: true,
      render: (scenario) => scenario.topology.raidLevel.toUpperCase(),
      compareBest: () => null
    },
    {
      id: 'drives',
      label: 'Drives',
      numeric: true,
      sortable: true,
      render: (scenario) => `${scenario.topology.drivesPerVdev}D×${scenario.topology.vdevCount}V`,
      compareBest: (_results, scenarios) => {
        const maxDrives = Math.max(...scenarios.map(s => s.topology.drivesPerVdev * s.topology.vdevCount));
        return maxDrives;
      }
    },
    {
      id: 'capacity',
      label: 'Capacity (TiB)',
      numeric: true,
      sortable: true,
      render: (_scenario, results) => results.capacity.practicalUsableTiB.toFixed(1),
      compareBest: (results) => Math.max(...results.map(r => r.capacity.practicalUsableTiB))
    },
    {
      id: 'efficiency',
      label: 'Efficiency (%)',
      numeric: true,
      sortable: true,
      render: (_scenario, results) => results.capacity.usablePercent.toFixed(0),
      compareBest: (results) => Math.max(...results.map(r => r.capacity.usablePercent))
    },
    {
      id: 'costPerTiB',
      label: 'Cost/TiB ($)',
      numeric: true,
      sortable: true,
      render: (_scenario, results) => results.economics.costPerUsableTB.toFixed(0),
      compareBest: (results) => Math.min(...results.map(r => r.economics.costPerUsableTB))
    },
    {
      id: 'risk',
      label: 'Risk (%)',
      numeric: true,
      sortable: true,
      render: (_scenario, results) => (results.reliability.totalDataLossProbability * 100).toFixed(3),
      compareBest: (results) => Math.min(...results.map(r => r.reliability.totalDataLossProbability))
    },
    {
      id: 'resilverTime',
      label: 'Resilver (h)',
      numeric: true,
      sortable: true,
      render: (_scenario, results) => results.reliability.resilverTimeHours.toFixed(1),
      compareBest: (results) => Math.min(...results.map(r => r.reliability.resilverTimeHours))
    },
    {
      id: 'iops',
      label: 'IOPS',
      numeric: true,
      sortable: true,
      render: (_scenario, results) => results.performance.effectiveIOPS.toFixed(0),
      compareBest: (results) => Math.max(...results.map(r => r.performance.effectiveIOPS))
    },
    {
      id: 'writeSpeed',
      label: 'Write (MB/s)',
      numeric: true,
      sortable: true,
      render: (_scenario, results) => results.performance.sequentialWriteMBps.toFixed(0),
      compareBest: (results) => Math.max(...results.map(r => r.performance.sequentialWriteMBps))
    }
  ];

  // Sort state
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
  };

  // Sort scenarios
  const sortedScenarios = [...scenarios].sort((a, b) => {
    if (!sortBy) return 0;

    const column = columns.find(c => c.id === sortBy);
    if (!column) return 0;

    const aVal = column.numeric ? parseFloat(String(column.render(a, resultsMap.get(a.id)!))) : String(column.render(a, resultsMap.get(a.id)!));
    const bVal = column.numeric ? parseFloat(String(column.render(b, resultsMap.get(b.id)!))) : String(column.render(b, resultsMap.get(b.id)!));

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Get best values for highlighting
  const getBestValue = (columnId: string) => {
    const column = columns.find(c => c.id === columnId);
    if (!column) return null;
    return column.compareBest(resultsArray, scenarios);
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (scenarios.length === 0) return;

    const headers = columns.map(c => c.label).join(',');
    const rows = sortedScenarios.map(scenario => {
      const results = resultsMap.get(scenario.id)!;
      return columns.map(c => {
        const value = c.render(scenario, results);
        // Quote string values that might contain commas
        return c.numeric ? value : `"${value}"`;
      }).join(',');
    });

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `scenario-comparison-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (scenarios.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Scenario Comparison Table
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No scenarios saved yet. Use the "Save Current as Scenario" button to compare configurations.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Scenario Comparison Table
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comparing {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''} side-by-side
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          disabled={scenarios.length === 0}
        >
          Export CSV
        </Button>
      </Box>

      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.numeric ? 'right' : 'left'}
                  sx={{
                    fontWeight: 'bold',
                    cursor: column.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={() => column.sortable && handleSort(column.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: column.numeric ? 'flex-end' : 'flex-start' }}>
                    {column.label}
                    {column.sortable && sortBy === column.id && (
                      <>
                        {sortDirection === 'asc' && <ArrowUpward fontSize="small" />}
                        {sortDirection === 'desc' && <ArrowDownward fontSize="small" />}
                      </>
                    )}
                  </Box>
                </TableCell>
              ))}
              {onCompare && (
                <TableCell align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedScenarios.map((scenario) => {
              const results = resultsMap.get(scenario.id);
              if (!results) return null;

              return (
                <TableRow key={scenario.id} hover>
                  {columns.map((column) => {
                    const value = column.render(scenario, results);
                    const bestValue = getBestValue(column.id);
                    const isBest = column.numeric && bestValue !== null && parseFloat(String(value)) === bestValue;

                    return (
                      <TableCell
                        key={column.id}
                        align={column.numeric ? 'right' : 'left'}
                        sx={{
                          backgroundColor: isBest ? 'success.lighter' : 'inherit',
                          fontWeight: isBest ? 'bold' : 'normal',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {isBest && (
                          <Chip label="Best" size="small" color="success" sx={{ height: 16, fontSize: '0.65rem', mr: 0.5 }} />
                        )}
                        {value}
                      </TableCell>
                    );
                  })}
                  {onCompare && (
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CompareIcon fontSize="small" />}
                        onClick={() => onCompare(scenario)}
                        sx={{ fontSize: '0.7rem' }}
                      >
                        Compare
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Tip:</strong> Click column headers to sort. Green highlighted cells show the best value in each column.
        </Typography>
      </Box>
    </Paper>
  );
}
